import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '../lib/i18n';

/**
 * What the person sees when a render throws.
 *
 * Without this, one bad render blanks the whole application: React unmounts the tree and the page
 * goes white, with the reason only in a console nobody has open.
 *
 * It is used twice, and the two are different jobs. Inside the shell, around the routed content:
 * the rail and the top bar have nothing to do with the failure, so they stay and only the content
 * area is replaced, and the boundary resets when the route changes — one that kept showing its
 * error after the person clicked somewhere else would have turned one broken screen into a broken
 * session. And at the very root, around the providers: a throw in the session or the preferences
 * provider happens *above* the shell, so nothing was catching it and the page simply went white.
 *
 * With no `resetKey` there is nothing to reset to — the same provider would throw again on the next
 * render — so the root one offers a reload instead of a retry.
 */

interface Props {
  children: ReactNode;
  /**
   * Changes when the route does, which is the signal to try rendering again.
   *
   * Absent at the root, where re-rendering cannot help: the offer there is a reload.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
  key: string | undefined;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, key: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey === state.key ? null : { error: null, key: props.resetKey };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is where a developer will look; the person sees the panel below. Reported
    // through the browser's own error channel rather than `console.error`, so it reaches the same
    // place an uncaught error would and the lint rule against stray logging still holds.
    reportError(Object.assign(error, { componentStack: info.componentStack }));
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const recoverable = this.props.resetKey !== undefined;

    return (
      <div className="view">
        <div className="empty empty--error" role="alert">
          <p className="empty__title">{t('boundary.title')}</p>
          <p className="muted">{recoverable ? t('boundary.body') : t('boundary.rootBody')}</p>
          <p className="muted small">
            <code>{this.state.error.message}</code>
          </p>

          <button
            type="button"
            className="button button--quiet"
            onClick={() =>
              recoverable ? this.setState({ error: null }) : window.location.reload()
            }
          >
            {recoverable ? t('state.retry') : t('boundary.reload')}
          </button>
        </div>
      </div>
    );
  }
}
